/* eslint-disable array-callback-return */
/* eslint-disable react/display-name */
/**
 * @file ManagementCrud.jsx
 * @author Fernando Ruano
 * @description ManagementCrud Component
 */

// Dependencies
import React, { useEffect } from 'react';
import {
  PlusCircleOutlined,
  EditOutlined,
  EyeOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
  MoreOutlined,
  FileSearchOutlined
} from '@ant-design/icons';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Popconfirm, Button, Menu, Dropdown } from 'antd';
import { useTranslation } from 'react-i18next';

// Components
import ButtonFilled from '@nephro/components/ButtonFilled';
import ManagementViewGenerator from '@nephro/components/ManagementViewGenerator';

// Styles
import styles from './styles.module.css';

/**
 * ManagementCrud Component
 * @param { Function } getItemsArray - getItems Action.
 * @param { Function } deleteItemById - deleteItem Action.
 * @param { Array } columns - Table columns.
 * @param { String } deleteConfirmText - Text to show into Delete Popconfirm.
 * @param { String } createButtonText - Create Button Text.
 * @param { String } itemIdKey - Key to get item id.
 * @param { Array } rowData - Row data list.
 * @param { Any } customMapDataAfterDelete - To launch custom function after delete.
 * @param { Bool } withDefaultActions - To show default actions.
 * @param { Bool } viewEyeIcon - To show default actions.
 * @param { Bool } search - To show input search.
 * @param { Bool } hasRowSelection - To show input search.
 * @param { isClientRendering } isClientRendering - To show input search.
 * @return { Node } React node.
 */
function ManagementCrud({
  getItemsArray,
  deleteItemById,
  columns,
  deleteConfirmText,
  createButtonText,
  itemIdKey,
  rowData,
  customMapDataAfterDelete,
  withDefaultActions,
  viewEyeIcon,
  search,
  hasRowSelection = false,
  isVirtualPagination,
  onRowSelection,
  setFilterData,
  searchTerm,
  searchableKeys,
  withPageSize
}) {
  const [columnsData, setColumnsData] = React.useState([]);
  const [clientSideFilteredRows, setClientSideFilteredRows] = React.useState(
    []
  );
  const [updatedColumns, setUpdatedColumns] = React.useState([]);
  const { t: translate } = useTranslation();
  const [current, setCurrent] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [lastFilters, setLastFilters] = React.useState({});

  const location = useLocation();
  const history = useHistory();

  // Get all items from backend
  const onGetItems = (_, response) => {
    if (response?.data) {
      const { content } = response?.data;
      if (!isVirtualPagination) {
        setTotal(response?.data.total_elements);
      }
      setColumnsData(content);
    }
  };

  const afterDelete = () => {
    getItemsArray({ paged: false }, customMapDataAfterDelete || onGetItems);
  };

  // Delete selected item by id
  const onDeleteItems = (id) => {
    deleteItemById({ id }, afterDelete);
  };

  // Navigate to route after click on action
  const onNavigateTo = (url) => {
    const baseUrl = location.pathname;
    const lastCharURL = baseUrl[baseUrl.length - 1];
    const goTo = lastCharURL === '/' ? `${url}` : `/${url}`;
    history.push(`${location.pathname}${goTo}`);
  };

  // Navigate to route on Mobile View
  const onNavigateToMobile = (url, id) => {
    if (url === 'delete') {
      onDeleteItems(id);
    } else {
      onNavigateTo(`${url}/${id}`);
    }
  };

  const manualFilter = (actualFilters) => {
    // Manual filter
    // eslint-disable-next-line consistent-return
    const filters = Object.entries(actualFilters).filter((filter) => {
      if (filter[1] !== null) {
        return filter;
      }
    });

    let filteredData = columnsData.filter((row) => {
      // eslint-disable-next-line consistent-return
      return filters?.every(([key, values]) => {
        return values && row[key] && values.includes(row[key]);
      });
    });

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();

      filteredData = filteredData.filter((row) => {
        // Verifica si alguno de los campos definidos en searchableKeys contiene el término de búsqueda
        return searchableKeys.some((key) => {
          const fieldValue = row[key];
          return (
            fieldValue &&
            fieldValue.toString().toLowerCase().includes(lowerCaseSearchTerm)
          );
        });
      });
    }

    if (setFilterData) setFilterData(filteredData);
    setClientSideFilteredRows(filteredData || []);
  };

  useEffect(() => {
    if (clientSideFilteredRows.length === 0) {
      setClientSideFilteredRows(columnsData);
      if (setFilterData) {
        setFilterData(columnsData);
        manualFilter(lastFilters);
      }
    }
  }, [columnsData]);

  const onChangePagination = (
    page,
    inUsageFilters,
    extras,
    inTimeRenderedRows
  ) => {
    if (inTimeRenderedRows.action === 'filter' && isVirtualPagination) {
      manualFilter(inUsageFilters);
      setLastFilters(inUsageFilters);
    } else {
      setCurrent(page.current - 1);
    }
  };

  const menu = (id) => (
    <Menu onClick={(e) => onNavigateToMobile(e.key, id)}>
      <Menu.Item key="edit">{translate('commons.edit')}</Menu.Item>
      <Menu.Item key="view">{translate('commons.details')}</Menu.Item>
      <Menu.Item key="delete">{translate('commons.delete')}</Menu.Item>
    </Menu>
  );
  React.useEffect(() => {
    if (rowData.length > 0) {
      setColumnsData(rowData.map((item, index) => ({ ...item, key: index })));
    } else if (customMapDataAfterDelete && rowData.length === 0) {
      getItemsArray({ paged: false }, customMapDataAfterDelete);
    } else {
      getItemsArray({ paged: false }, onGetItems);
    }

    return () => {
      setColumnsData([]);
    };
  }, []);

  React.useEffect(() => {
    if (customMapDataAfterDelete) {
      setColumnsData(rowData);
    }
  }, [rowData]);

  React.useEffect(() => {
    manualFilter(lastFilters);
  }, [columnsData, searchTerm, rowData]);

  React.useEffect(() => {
    const actionsColumn = {
      title: translate('commons.actions'),
      dataIndex: 'actions',
      fixed: 'right',
      width: 100,
      render: (_, item) => {
        const id = item[itemIdKey];
        return (
          <div>
            <div className="w-full min-w-min max-w-2 lg:w-20 flex justify-between items-center hidden md:flex">
              <EditOutlined
                onClick={() => onNavigateTo(`edit/${id}`)}
                className={styles.buttonIcon}
              />
              {viewEyeIcon ? (
                <EyeOutlined
                  className={styles.buttonIcon}
                  onClick={() => onNavigateTo(`view/${id}`)}
                />
              ) : (
                <FileSearchOutlined
                  className={styles.buttonIcon}
                  onClick={() => onNavigateTo(`view/${id}`)}
                />
              )}
              <Popconfirm
                title={translate(deleteConfirmText)}
                cancelText={translate('commons.cancel')}
                okText={translate('commons.confirm')}
                onConfirm={() => onDeleteItems(id)}
                icon={<QuestionCircleOutlined style={{ color: '#FA7268' }} />}>
                <DeleteOutlined className={styles.errorColor} />
              </Popconfirm>
            </div>
            <div className="md:hidden flex justify-center items-center">
              <Dropdown overlay={() => menu(id)} trigger={['click']}>
                <Button
                  key="3"
                  className={styles.moreButton}
                  icon={<MoreOutlined className={styles.buttonIcon} />}
                />
              </Dropdown>
            </div>
          </div>
        );
      }
    };
    if (withDefaultActions) columns.push(actionsColumn);
    setUpdatedColumns([...columns]);
  }, [columns]);

  // Options to show at right
  const rightOptions = createButtonText
    ? [
        <ButtonFilled
          key="1"
          type="blue"
          onActionClick={() => onNavigateTo('create')}
          textButton={createButtonText}
          iconComponent={<PlusCircleOutlined className={styles.buttonIcon} />}
        />
      ]
    : [];

  return (
    <div className="h-full">
      <ManagementViewGenerator
        rightOptions={rightOptions}
        columns={updatedColumns}
        data={clientSideFilteredRows}
        filter={false}
        search={search}
        hasRowSelection={hasRowSelection}
        isVirtualPagination
        pagination={{ total, current, isVirtualPagination }}
        onChangePagination={onChangePagination}
        onRowSelection={(rows) => onRowSelection(rows)}
        withPageSize={withPageSize}
      />
    </div>
  );
}

/**
 * Component definition of proptypes.
 * @param { Function } getItemsArray - getItems Action.
 * @param { Function } deleteItemById - deleteItem Action.
 * @param { Array } columns - Table columns.
 * @param { String } deleteConfirmText - Text to show into Delete Popconfirm.
 * @param { String } createButtonText - Create Button Text.
 * @param { String } itemIdKey - Key to get item id.
 * @param { Array } rowData - Row data list.
 * @param { Any } customMapDataAfterDelete - To launch custom function after delete.
 * @param { Bool } withDefaultActions - To show default actions.
 * @param { Bool } viewEyeIcon - To show default actions.
 */
ManagementCrud.propTypes = {
  getItemsArray: PropTypes.func.isRequired,
  deleteItemById: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  deleteConfirmText: PropTypes.string.isRequired,
  createButtonText: PropTypes.string,
  itemIdKey: PropTypes.string.isRequired,
  rowData: PropTypes.array,
  customMapDataAfterDelete: PropTypes.any,
  withDefaultActions: PropTypes.bool,
  viewEyeIcon: PropTypes.bool,
  search: PropTypes.bool,
  hasRowSelection: PropTypes.bool,
  isVirtualPagination: PropTypes.bool,
  onRowSelection: PropTypes.func,
  setFilterData: PropTypes.func,
  searchTerm: PropTypes.string,
  searchableKeys: PropTypes.array,
  withPageSize: PropTypes.bool
};

/**
 * Default properties.
 */
ManagementCrud.defaultProps = {
  rowData: [],
  customMapDataAfterDelete: null,
  withDefaultActions: true,
  viewEyeIcon: true,
  search: true,
  onRowSelection: () => {},
  searchableKeys: [],
  searchTerm: ''
};

export default ManagementCrud;
